<template>
  <!-- horizon top nav (FI-1869): edge-attached translucent steelblue bar per
       the v27 unified-toolbar treatment. nav toggle + brand on the left;
       notifications bell (desktop), help menu, and the account avatar
       on the right (classic parity: navbar.vue keeps help + avatar in the
       bar). section navigation lives entirely in the rail tree. class names
       deliberately avoid .nav-link/.nav-tabs/.bg-info (navbar.vue's unscoped
       styles restyle those app-wide even unmounted), but the popovers reuse
       navbar's global .nc-msg-* / .profile-* styles on purpose so both
       surfaces stay pixel-identical. -->
  <nav class="fi-tn" aria-label="Primary">
    <div class="fi-tn-bar">
      <!-- mobile only: opens the drawer. on desktop the collapse toggle
           lives at the foot of the rail in both states (chaz round 9) -->
      <button
        v-if="isMobile"
        v-b-tooltip.hover.bottom="barTip(navState.mobileOpen ? 'Close navigation' : 'Open navigation')"
        class="fi-tn-toggle"
        :aria-label="navState.mobileOpen ? 'Close navigation' : 'Open navigation'"
        :aria-expanded="navState.mobileOpen ? 'true' : 'false'"
        aria-controls="fi-sn-drawer"
        @click="onToggleNav"
      >
        <!-- standard panel-left glyph: left column filled while the drawer
             is open, outline otherwise (fa 4.7 has no sidebar icon) -->
        <svg class="fi-tn-toggle-glyph" viewBox="0 0 20 16" aria-hidden="true">
          <rect x="1" y="1" width="18" height="14" rx="3" fill="none" stroke="currentColor" stroke-width="1.6"/>
          <line x1="7.5" y1="1.8" x2="7.5" y2="14.2" stroke="currentColor" stroke-width="1.6"/>
          <rect v-if="navState.mobileOpen" x="2" y="2" width="4.6" height="12" rx="1.6" fill="currentColor" stroke="none"/>
        </svg>
      </button>
      <!-- on phone widths the lockup centers in the bar (ios-style title):
           position says "identity, not action", which the left-aligned bare
           glyph next to the drawer toggle failed to. -->
      <div class="fi-tn-brand">
        <!-- the canonical cloud-with-arrows mark (LogoMark.svg / www logo.svg)
             inlined verbatim with currentColor so it renders white here -->
        <svg class="fi-tn-brand-logo" viewBox="-2 0 200 128" fill="none" aria-hidden="true">
          <g stroke="currentColor" stroke-width="8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M36 120 C18 120 4 106 4 88 C4 74 14 62 28 57 C26 42 36 30 52 30 C58 30 64 32 68 36 C76 18 92 8 110 8 C138 8 160 30 162 57 C180 60 192 76 192 92 C192 108 178 120 162 120 Z"/>
            <path d="M65 64 H133 M116 48 L133 64 L116 80"/>
            <path d="M133 92 H65 M82 76 L65 92 L82 108"/>
          </g>
        </svg>
        <span class="fi-tn-brand-name">Fleet Integrations</span>
      </div>

      <!-- desktop only: the fleet assistant's entry point is an ask box in
           the bar (the floating pill is gone under horizon -- the launcher
           still mounts, it just never renders its pill). it behaves like a
           search field: the question is composed here, and ENTER expands the
           box into the panel -- which drops down out of it -- and sends the
           question. typing alone never moves focus or opens anything.
           the avatar glyph is the user's chosen FA icon and plays its
           attention animation while a reply finished unread behind a closed
           panel. -->
      <form
        v-if="!isMobile && faAvailable"
        ref="askForm"
        class="fi-tn-ask-wrap"
        role="search"
        @submit.prevent="submitAsk"
      >
        <div class="fi-tn-ask" :class="{ 'fi-tn-ask--focused': askFocused, 'fi-tn-ask--unread': faUnread }">
          <fa_avatar class="fi-tn-ask-glyph" :variant="avatarVariant" :active="faUnread" />
          <!-- a textarea, not an input, purely so a long question wraps and
               grows the box downward instead of scrolling sideways. it stays
               a single-line control in every other respect: enter submits and
               never inserts a newline, so askText is always one line of text.
               autosize caps at four rows, then scrolls. -->
          <textarea
            ref="ask"
            v-model="askText"
            class="fi-tn-ask-input"
            rows="1"
            placeholder="Search or Ask Fleet Assistant"
            aria-label="Search or Ask Fleet Assistant"
            autocomplete="off"
            role="combobox"
            aria-autocomplete="list"
            aria-controls="fi-tn-ask-suggestions"
            :aria-expanded="suggestionsOpen ? 'true' : 'false'"
            :aria-activedescendant="activeSuggestionId"
            @focus="onAskFocus"
            @blur="onAskBlur"
            @input="onAskInput"
            @keydown.enter.prevent="submitAsk"
            @keydown.down.prevent="moveSuggestion(1)"
            @keydown.up.prevent="moveSuggestion(-1)"
            @keydown.esc.stop.prevent="onAskEscape"
          ></textarea>
        </div>
        <!-- sectioned typeahead: questions asked before (localStorage,
             written on every send from any assistant surface), then matching
             OBJECTS (drivers, vehicles, bookings, users, ops events -- from
             the api, debounced), then matching pages. picking a question
             asks it; picking an object or page NAVIGATES and never involves
             the assistant.
             one flat askOptions list backs every section, because the arrow
             keys have to walk across the boundaries; askGroups only re-cuts
             that same list into its sections for rendering and carries each
             row's ORIGINAL index, so ids, aria and the handlers all still
             speak in askOptions terms.
             grouping is what lets the site map fallback lay its sections out
             in columns (--map below) instead of one very long strip -- a few
             typeahead matches read fine stacked, a whole site map does not.
             mousedown rather than click: the input's blur fires first and
             would close the list before a click ever landed. -->
        <ul
          v-if="suggestionsOpen"
          id="fi-tn-ask-suggestions"
          class="fi-tn-ask-list"
          :class="{ 'fi-tn-ask-list--map': sitemapShowing }"
          role="listbox"
          :aria-label="sitemapShowing ? 'Site map' : 'Recent questions and matching results'"
        >
          <li
            v-for="g in askGroups"
            :key="g.key"
            class="fi-tn-ask-group"
            role="group"
            :aria-label="g.section"
          >
            <div class="fi-tn-ask-hd" aria-hidden="true">{{ g.section }}</div>
            <div
              v-for="row in g.rows"
              :id="'fi-tn-ask-opt-' + row.i"
              :key="row.o.key"
              class="fi-tn-ask-opt"
              :class="{
                'fi-tn-ask-opt--active': row.i === activeSuggestion,
                'fi-tn-ask-opt--more': row.o.type === 'more',
              }"
              role="option"
              :aria-selected="row.i === activeSuggestion ? 'true' : 'false'"
              @mousedown.prevent="chooseSuggestion(row.i)"
              @mouseenter="activeSuggestion = row.i"
            >
              <i
                class="fa fi-tn-ask-opt-icon"
                :class="row.o.icon || 'fa-history'"
                aria-hidden="true"
              ></i>
              <span class="fi-tn-ask-opt-text">{{ row.o.label }}</span>
              <span v-if="row.o.hint" class="fi-tn-ask-opt-hint">{{ row.o.hint }}</span>
              <!-- same star, same store, as the site map page's tiles and the
                   page headers': favoritableDestination resolves the row to a
                   nav destination and nav_favorites owns the rest, so a star
                   set here shows up in the rail and the favorites bar without
                   this component knowing they exist.
                   mousedown, like the rows themselves, because the input's
                   blur would otherwise close the list before a click landed:
                   .stop keeps the row underneath from navigating and .prevent
                   keeps the focus (and so the panel) where it is. -->
              <button
                v-if="canFavoriteRow(row.o)"
                type="button"
                tabindex="-1"
                class="fi-tn-ask-star"
                :class="{ 'fi-tn-ask-star--on': isRowFavorite(row.o) }"
                :title="isRowFavorite(row.o) ? 'Remove from Favorites' : 'Add to Favorites'"
                :aria-pressed="isRowFavorite(row.o) ? 'true' : 'false'"
                :aria-label="(isRowFavorite(row.o) ? 'Remove ' : 'Add ') + row.o.label + (isRowFavorite(row.o) ? ' from' : ' to') + ' Favorites'"
                @mousedown.prevent.stop="toggleRowFavorite(row.o)"
              >
                <i class="fa" :class="isRowFavorite(row.o) ? 'fa-star' : 'fa-star-o'" aria-hidden="true"></i>
              </button>
            </div>
          </li>
        </ul>
      </form>

      <div class="fi-tn-spacer"></div>

      <!-- mobile only: fleet assistant rides the bar instead of floating
           over content (the launcher hides its pill under 992 when the
           horizon shell sets bar-launches). glyph is the user's chosen FA
           avatar; it plays its attention animation while a reply finished
           unread behind a closed panel -->
      <button
        v-if="isMobile && faAvailable"
        v-b-tooltip.hover.bottom="barTip('Fleet Assistant')"
        class="fi-tn-fa"
        aria-label="Fleet Assistant"
        @click.stop="openFa()"
      >
        <fa_avatar class="fi-tn-fa-glyph" :variant="avatarVariant" :active="faUnread" />
      </button>

      <!-- tenant switcher, classic parity (navbar.vue:429-431 keeps one in the
           bar). anyone with more than one tenant gets it; the copy-id button
           beside it stays staff-only, as in the rail.
           this is the ONLY tenant dropdown whenever it renders -- the rail
           drops its own copy while this one is up (both read
           navState.railHidden, so they cannot both decide to show). below the
           rail-hidden breakpoint the bar has no room for it and the rail's
           copy takes over. -->
      <div v-if="showTenantSelect" class="fi-tn-tenant">
        <!-- copy-tenant-id: the glyph is gone but the hit area stays, so the
             bar reads as just the switcher while the shortcut survives. it
             keeps its aria-label and tooltip, which is how anyone finds it --
             an empty button is invisible to the eye but not to hover or to a
             screen reader. it sits to the LEFT of the switcher: on the right
             it fell between the switcher and the bell, where an invisible hit
             area is the last thing anyone expects to find. -->
        <button
          v-if="ctx.isEffectiveAdmin"
          v-b-tooltip.hover.bottom="barTip('Copy tenant id (' + user.tenant + ')')"
          class="fi-tn-tenant-copy"
          :aria-label="'Copy tenant id (' + user.tenant + ')'"
          @click.stop="copyTenant"
        ></button>
        <!-- the well span exists only to anchor the css chevron: a native
             select cannot carry child content, and the chevron has to track
             the select's own right edge, not the group's -->
        <span class="fi-tn-tenant-well">
          <select
            v-model="user.tenant"
            class="fi-tn-tenant-select"
            aria-label="Tenant"
            @change="onTenantChange"
          >
            <option v-for="opt in tenantOptions" :key="optValue(opt)" :value="optValue(opt)">{{ optText(opt) }}</option>
          </select>
        </span>
      </div>

      <!-- staff shortcut: support tickets are a daily stop for admins, and the
           only other way there is two clicks through the help menu. desktop
           only, like the help icon beside it -- the phone bar has no room and
           the rail's help section already carries the destination. -->
      <router-link
        v-if="!isMobile && ctx.isEffectiveAdmin"
        v-b-tooltip.hover.bottom="barTip('Support Tickets')"
        :to="{ name: 'help_center', query: { tab: 'support' } }"
        class="fi-tn-support"
        aria-label="Support Tickets"
      >
        <i class="fa fa-ticket" aria-hidden="true"></i>
      </router-link>

      <!-- desktop only: on mobile the notifications live in the account
           panel (classic desktop dropdown parity), so the bell hides -->
      <div class="fi-tn-bell-wrap">
        <button
          v-b-tooltip.hover.bottom="barTip('Notifications')"
          class="fi-tn-bell"
          aria-label="Notifications"
          aria-haspopup="true"
          :aria-expanded="notificationsOpen ? 'true' : 'false'"
          @click.stop="toggleNotifications"
        >
          <i class="fa fa-bell-o" aria-hidden="true"></i>
          <span v-if="unreadCount > 0" class="profile-avatar-badge">{{ unreadBadgeText }}</span>
        </button>

        <!-- notifications popover: the FI-1432 iOS-Messages card list, so
             opening it never navigates the user away from their page -->
        <div v-if="notificationsOpen" class="fi-tn-nc-pop" @click.stop>
          <FiNotificationList @closed="closeAll" />
        </div>
      </div>

      <!-- help menu: the classic bar linked straight to the help center; the
           horizon icon opens the user's help options plus the legal pages.
           desktop only (chaz round 12: the phone bar was crowding); phones
           reach help center / legal / the assistant through the rail's help
           section instead (round 13) -->
      <div v-if="!isMobile" class="fi-tn-help-wrap">
        <button
          v-b-tooltip.hover.bottom="barTip('Help')"
          class="fi-tn-help"
          aria-label="Help"
          aria-haspopup="true"
          :aria-expanded="helpOpen ? 'true' : 'false'"
          @click.stop="toggleHelp"
        >
          <i class="fa fa-question-circle" aria-hidden="true"></i>
        </button>
        <div v-if="helpOpen" class="fi-tn-help-pop" @click.stop>
          <template v-for="(g, gi) in helpMenu">
            <div v-if="gi > 0" :key="g.key + '-div'" class="fi-tn-pop-divider"></div>
            <div v-if="g.label" :key="g.key + '-label'" class="fi-tn-menu-label">{{ g.label }}</div>
            <template v-for="mi in g.items">
              <a
                v-if="mi.href"
                :key="mi.key"
                :href="mi.href"
                target="_blank"
                rel="noopener"
                class="fi-tn-menu-item"
                @click="closeAll"
              >
                <i class="fa fi-tn-menu-icon" :class="mi.icon" aria-hidden="true"></i>
                <span>{{ mi.label }}</span>
                <i class="fa fa-external-link fi-tn-menu-ext" aria-hidden="true"></i>
              </a>
              <router-link
                v-else
                :key="mi.key"
                :to="mi.to"
                class="fi-tn-menu-item"
                @click.native="closeAll"
              >
                <i class="fa fi-tn-menu-icon" :class="mi.icon" aria-hidden="true"></i>
                <span>{{ mi.label }}</span>
              </router-link>
            </template>
          </template>
        </div>
      </div>

      <!-- account avatar: desktop opens the profile popover (notifications
           stay in the bell); mobile opens the combined account panel below,
           so the avatar carries the unread badge there instead. the circle
           itself is navbar's global .profile-avatar so it matches classic
           exactly (no outline) -->
      <div class="fi-tn-avatar-wrap">
        <button
          v-b-tooltip.hover.bottom="barTip(displayName)"
          class="fi-tn-avatar-btn"
          aria-label="Account"
          aria-haspopup="true"
          :aria-expanded="avatarOpen ? 'true' : 'false'"
          @click.stop="toggleAvatar"
        >
          <span class="profile-avatar">
            {{ initials }}
            <span v-if="isMobile && unreadCount > 0" class="profile-avatar-badge">{{ unreadBadgeText }}</span>
          </span>
        </button>

        <!-- desktop profile popover: markup + classes lifted from navbar's
             profile dropdown (identity card header, role tag, icon actions,
             theme toggle), styled by its global .profile-* rules -->
        <div v-if="avatarOpen && !isMobile" class="fi-tn-profile-pop" @click.stop>
          <div class="profile-dropdown-header">
            <span class="profile-dd-avatar" :title="displayName">{{ initials }}</span>
            <div class="profile-dd-identity">
              <div class="profile-name-row">
                <span class="profile-display-name">{{ displayName }}</span>
                <button
                  v-if="user.role === 'admin'"
                  type="button"
                  class="profile-role-tag profile-role-tag--interactive"
                  :class="viewAsNonAdmin ? 'profile-role-tag-muggle' : 'profile-role-tag-admin'"
                  :title="viewAsNonAdmin ? 'Switch back to the admin view' : 'Preview the app as a non-admin'"
                  @click.stop.prevent="toggleViewAs"
                >
                  <i class="fa" :class="viewAsNonAdmin ? 'fa-magic fa-flip-horizontal' : 'fa-shield'" aria-hidden="true"></i>
                  <span>{{ viewAsNonAdmin ? 'MUGGLE' : 'ADMIN' }}</span>
                </button>
                <span
                  v-else
                  class="profile-role-tag"
                  :class="'profile-role-tag-' + (user.role || 'other')"
                >
                  <i class="fa" :class="roleIconFor(user.role)" aria-hidden="true"></i>
                  <span>{{ roleDisplayLabel(user.role) }}</span>
                </span>
              </div>
              <div class="profile-email">{{ user.username }}</div>
            </div>
          </div>
          <template v-if="ctx.canPickTheme">
            <div class="fi-tn-pop-divider"></div>
            <div class="profile-theme-row" @click.stop>
              <span class="profile-theme-label">Theme</span>
              <div class="profile-theme-toggle" role="group" aria-label="Theme">
                <button
                  type="button"
                  class="profile-theme-opt"
                  :class="{ 'profile-theme-opt--active': themeChoice === 'original' }"
                  :aria-pressed="String(themeChoice === 'original')"
                  @click.stop.prevent="setSiteTheme('original')"
                >
                  <span class="profile-theme-swatches" aria-hidden="true">
                    <span class="profile-theme-swatch" style="background:#4682b4"></span>
                    <span class="profile-theme-swatch" style="background:#007bff"></span>
                    <span class="profile-theme-swatch" style="background:#b58100"></span>
                  </span>
                  <span>Classic</span>
                </button>
                <button
                  type="button"
                  class="profile-theme-opt"
                  :class="{ 'profile-theme-opt--active': themeChoice === 'new' }"
                  :aria-pressed="String(themeChoice === 'new')"
                  @click.stop.prevent="setSiteTheme('new')"
                >
                  <span class="profile-theme-swatches" aria-hidden="true">
                    <span class="profile-theme-swatch" style="background:#15324d"></span>
                    <span class="profile-theme-swatch" style="background:#007ac7"></span>
                    <span class="profile-theme-swatch" style="background:#7b3fb0"></span>
                  </span>
                  <span>Horizon</span>
                </button>
                <button
                  type="button"
                  class="profile-theme-opt"
                  :class="{ 'profile-theme-opt--active': themeChoice === 'midnight' }"
                  :aria-pressed="String(themeChoice === 'midnight')"
                  @click.stop.prevent="setSiteTheme('midnight')"
                >
                  <span class="profile-theme-swatches" aria-hidden="true">
                    <span class="profile-theme-swatch" style="background:#0e1b2b"></span>
                    <span class="profile-theme-swatch" style="background:#6fb1e8"></span>
                    <span class="profile-theme-swatch" style="background:#7b3fb0"></span>
                  </span>
                  <span>Midnight</span>
                </button>
              </div>
            </div>
          </template>
          <!-- account actions as list rows under the theme picker (chaz
               round 7: replaces the icon chips in the header's top-right;
               the mobile panel keeps the classic header chips) -->
          <div class="fi-tn-pop-divider"></div>
          <router-link
            :to="{ name: 'user_detail', query: { username: user.username } }"
            class="fi-tn-menu-item"
            @click.native="closeAll"
          >
            <i class="fa fa-user-o fi-tn-menu-icon" aria-hidden="true"></i>
            <span>Manage Profile</span>
          </router-link>
          <button type="button" class="fi-tn-menu-item fi-tn-menu-item--danger" @click="logout">
            <i class="fa fa-sign-out fi-tn-menu-icon" aria-hidden="true"></i>
            <span>Logout</span>
          </button>
        </div>
      </div>
    </div>

    <!-- mobile account panel: the classic desktop dropdown (identity card +
         theme + notifications) rendered as a full page-sized sheet below the
         bar, per the horizon mobile design -->
    <div v-if="avatarOpen && isMobile" class="fi-tn-account-panel" @click.stop>
      <div class="profile-dropdown-header">
        <span class="profile-dd-avatar" :title="displayName">{{ initials }}</span>
        <div class="profile-dd-identity">
          <div class="profile-name-row">
            <span class="profile-display-name">{{ displayName }}</span>
            <button
              v-if="user.role === 'admin'"
              type="button"
              class="profile-role-tag profile-role-tag--interactive"
              :class="viewAsNonAdmin ? 'profile-role-tag-muggle' : 'profile-role-tag-admin'"
              :title="viewAsNonAdmin ? 'Switch back to the admin view' : 'Preview the app as a non-admin'"
              @click.stop.prevent="toggleViewAs"
            >
              <i class="fa" :class="viewAsNonAdmin ? 'fa-magic fa-flip-horizontal' : 'fa-shield'" aria-hidden="true"></i>
              <span>{{ viewAsNonAdmin ? 'MUGGLE' : 'ADMIN' }}</span>
            </button>
            <span
              v-else
              class="profile-role-tag"
              :class="'profile-role-tag-' + (user.role || 'other')"
            >
              <i class="fa" :class="roleIconFor(user.role)" aria-hidden="true"></i>
              <span>{{ roleDisplayLabel(user.role) }}</span>
            </span>
          </div>
          <div class="profile-email">{{ user.username }}</div>
        </div>
        <div class="profile-dd-actions">
          <b-button
            size="sm"
            variant="outline-secondary"
            :to="{ name: 'user_detail', query: { username: user.username } }"
            class="profile-dd-btn"
            title="Manage Profile"
            aria-label="Manage Profile"
            @click.native="closeAll"
          >
            <i class="fa fa-user-o" aria-hidden="true"></i>
          </b-button>
          <b-button
            size="sm"
            variant="outline-danger"
            class="profile-dd-btn"
            title="Logout"
            aria-label="Logout"
            @click="logout"
          >
            <i class="fa fa-sign-out" aria-hidden="true"></i>
          </b-button>
        </div>
      </div>
      <template v-if="ctx.canPickTheme">
        <div class="fi-tn-pop-divider"></div>
        <div class="profile-theme-row" @click.stop>
          <span class="profile-theme-label">Theme</span>
          <div class="profile-theme-toggle" role="group" aria-label="Theme">
            <button
              type="button"
              class="profile-theme-opt"
              :class="{ 'profile-theme-opt--active': themeChoice === 'original' }"
              :aria-pressed="String(themeChoice === 'original')"
              @click.stop.prevent="setSiteTheme('original')"
            >
              <span class="profile-theme-swatches" aria-hidden="true">
                <span class="profile-theme-swatch" style="background:#4682b4"></span>
                <span class="profile-theme-swatch" style="background:#007bff"></span>
                <span class="profile-theme-swatch" style="background:#b58100"></span>
              </span>
              <span>Classic</span>
            </button>
            <button
              type="button"
              class="profile-theme-opt"
              :class="{ 'profile-theme-opt--active': themeChoice === 'new' }"
              :aria-pressed="String(themeChoice === 'new')"
              @click.stop.prevent="setSiteTheme('new')"
            >
              <span class="profile-theme-swatches" aria-hidden="true">
                <span class="profile-theme-swatch" style="background:#15324d"></span>
                <span class="profile-theme-swatch" style="background:#007ac7"></span>
                <span class="profile-theme-swatch" style="background:#7b3fb0"></span>
              </span>
              <span>Horizon</span>
            </button>
            <button
              type="button"
              class="profile-theme-opt"
              :class="{ 'profile-theme-opt--active': themeChoice === 'midnight' }"
              :aria-pressed="String(themeChoice === 'midnight')"
              @click.stop.prevent="setSiteTheme('midnight')"
            >
              <span class="profile-theme-swatches" aria-hidden="true">
                <span class="profile-theme-swatch" style="background:#0e1b2b"></span>
                <span class="profile-theme-swatch" style="background:#6fb1e8"></span>
                <span class="profile-theme-swatch" style="background:#7b3fb0"></span>
              </span>
              <span>Midnight</span>
            </button>
          </div>
        </div>
      </template>
      <div class="fi-tn-pop-divider"></div>
      <FiNotificationList @closed="closeAll" />
    </div>
  </nav>
</template>

<script>
import { mapState } from 'vuex'
import { update_site_theme, search_objects } from '@/networking/apis'
import { buildNavContext, resolveHelpMenu, faLauncherAvailable } from '@/nav/registry'
import { navState, toggleCollapsed, setMobileOpen, noHoverDevice, railHiddenNow } from '@/nav/nav_state'
import { roleIconForInternal, roleLabelForInternal } from '@/helpers/role_display'
import {
  matchRecents, rememberDestination, ensureRecentQuestionsLoaded,
} from '@/utils/fa_recent_questions'
import { searchPages, siteMapPages } from '@/utils/page_search'
import { favoritableDestination } from '@/utils/site_map_pages'
import { favState, isFavorite, toggleFavorite, ensureFavoritesLoaded } from '@/nav/nav_favorites'
import FiNotificationList from './fi_notification_list.vue'
import fa_avatar from '../fa_avatar.vue'
import { copyToClipboard } from '@/helpers/vsm_standalone_url'

// ask box autosize: must match the .fi-tn-ask-input line-height in css, and
// caps how far the box grows before it scrolls instead
const ASK_LINE_HEIGHT = 18
const ASK_MAX_ROWS = 4
// how many pages of one section the site map fallback shows before it offers
// the rest behind a Show more row. sized so the usual sections show whole and
// only the long ones (reports, on a tenant with the full package) get cut.
const SITEMAP_SECTION_CAP = 15


export default {
  name: 'fi_top_nav',
  components: { FiNotificationList, fa_avatar },
  data() {
    return {
      navState,
      notificationsOpen: false,
      helpOpen: false,
      avatarOpen: false,
      // mirrors the launcher's hasUnread via the fa-unread-change event
      faUnread: false,
      // the desktop ask box; cleared as soon as the question is handed to
      // the panel, so the bar never holds a stale query
      askText: '',
      askFocused: false,
      // typeahead over previously asked questions: matches for what is
      // currently typed, and which one arrow keys have landed on (-1 = none,
      // meaning enter sends the typed text as-is)
      suggestions: [],
      // pages matching what is typed; the last typeahead section
      pageMatches: [],
      // objects (drivers, vehicles, bookings, users, ops events) matching
      // what is typed, from the api on a debounce; grouped by type between
      // the recents and the pages
      objectMatches: [],
      // indexes askOptions, all sections flattened -- NOT suggestions
      activeSuggestion: -1,
      // the site map fallback is showing: set whenever the list would open
      // onto nothing (entering the box, clearing it, or down-arrow), and
      // dropped again the moment anything real matches
      sitemapOpen: false,
      // site map sections whose Show more row has been used, by section title.
      // lasts as long as the list is open and is forgotten when it closes --
      // an expansion is an answer to "where is that report", not a setting.
      expandedSections: [],
      // held in data so the rows re-render when the shared favorites store
      // changes -- from a star here, from the site map page, or from the rail
      // (same trick as fi_fav_star.vue and site_map.vue)
      favState,
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
      viewAs: state => state.profile.view_as,
      siteTheme: state => state.profile.site_theme,
      unreadCount: state => state.notifications.unreadCount,
    }),
    ctx() {
      return buildNavContext(this.user, this.viewAs)
    },
    helpMenu() {
      return resolveHelpMenu(this.ctx)
    },
    faAvailable() {
      // same gate as the floating pill in App.vue
      return faLauncherAvailable(this.user, this.$route.name)
    },
    // the rail-hidden breakpoint, from the one shared observable rather than
    // a matchMedia listener of our own: the rail reads the same value to
    // decide who owns the tenant switcher, and two listeners that merely
    // agree today are a drift waiting to happen
    isMobile() {
      return this.navState.railHidden
    },
    // every section as ONE list, which is what the arrow keys walk and what
    // activeSuggestion indexes. `first` marks each section's opening row so
    // the template can render a heading above it without a second v-for
    // getting the indices out of step. order: recents, then objects grouped
    // by type, then pages -- objects sit above pages because a typed name is
    // far more often a hunt for a driver or booking than for a screen.
    askOptions() {
      const out = []
      // one Recent section holding both histories in recency order: an entry
      // with a kind was somewhere the user went (and replays as navigation),
      // one without was something they asked. they keep their own icons so the
      // two read apart without needing separate headings.
      this.suggestions.forEach((e, i) => {
        out.push({
          type: e.kind ? 'recent_destination' : 'recent',
          section: 'Recent',
          key: (e.kind || 'q') + ':' + e.q,
          label: e.q,
          icon: e.icon || (e.kind ? 'fa-arrow-right' : 'fa-history'),
          recent: e,
          first: i === 0,
        })
      })
      const objectMeta = {
        driver: { section: 'Drivers', icon: 'fa-user-o' },
        vehicle: { section: 'Vehicles', icon: 'fa-bus' },
        booking: { section: 'Bookings', icon: 'fa-calendar-check-o' },
        user: { section: 'Users', icon: 'fa-users' },
        ops_event: { section: 'Events', icon: 'fa-clipboard' },
      }
      Object.keys(objectMeta).forEach(type => {
        this.objectMatches.filter(m => m.type === type).forEach((m, i) => {
          out.push({
            type: 'object',
            section: objectMeta[type].section,
            key: 'o:' + type + ':' + m.id,
            label: m.label,
            hint: m.sublabel || '',
            icon: objectMeta[type].icon,
            object: m,
            first: i === 0,
          })
        })
      })
      this.pageMatches.forEach((p, i) => {
        out.push({
          type: 'page',
          section: 'Pages',
          key: 'p:' + p.id,
          label: p.name,
          hint: p.sectionTitle || '',
          icon: p.icon || 'fa-file-o',
          page: p,
          first: i === 0,
        })
      })
      // nothing matched and nothing was recently asked: down-arrow answers with
      // the site map instead of a dead key -- the site map's own sections, in
      // its own order, every page under each. no hint per row: the heading
      // above it already names the section, which is the only thing the Pages
      // section uses the hint for. deliberately last and deliberately
      // conditional on an otherwise EMPTY list -- a real match always wins, so
      // this can never push results down the list.
      if (this.sitemapOpen && !out.length) {
        const order = []
        const bySection = new Map()
        siteMapPages(this.user, this.viewAs).forEach(p => {
          const title = p.sectionTitle || 'Site map'
          if (!bySection.has(title)) {
            bySection.set(title, [])
            order.push(title)
          }
          bySection.get(title).push(p)
        })
        order.forEach(title => {
          const pages = bySection.get(title)
          // a tenant with the full reports package runs to dozens of report
          // pages, and one section that long sets the height of every column
          // beside it. the tail is a click away instead.
          const shown = this.expandedSections.indexOf(title) === -1
            ? pages.slice(0, SITEMAP_SECTION_CAP)
            : pages
          shown.forEach((p, i) => {
            out.push({
              type: 'page',
              section: title,
              key: 's:' + p.id,
              label: p.name,
              icon: p.icon || 'fa-file-o',
              page: p,
              first: i === 0,
              // marks the whole list as the map, which is what widens it into
              // columns -- reading it back off the options beats re-deriving
              // "did the fallback win" from the four things it depends on
              map: true,
            })
          })
          if (pages.length > shown.length) {
            // a real option, not a footer: the arrow keys have to be able to
            // reach it or the tail is mouse-only
            out.push({
              type: 'more',
              section: title,
              key: 'm:' + title,
              label: 'Show ' + (pages.length - shown.length) + ' more',
              icon: 'fa-ellipsis-h',
              first: false,
              map: true,
            })
          }
        })
      }
      return out
    },
    // askOptions re-cut into its sections, each row keeping its index in the
    // FLAT list: the template renders groups, everything else (arrow keys,
    // aria-activedescendant, chooseSuggestion) still indexes askOptions, and
    // the two can't drift because the indices come from the same walk.
    askGroups() {
      const groups = []
      this.askOptions.forEach((o, i) => {
        if (o.first || !groups.length) {
          groups.push({ key: 'g:' + o.key, section: o.section, rows: [] })
        }
        groups[groups.length - 1].rows.push({ o, i })
      })
      return groups
    },
    // the site map fallback is what's showing, so the panel goes wide and
    // multi-column. only ever true when nothing else matched (askOptions only
    // ever builds the map rows into an otherwise empty list).
    sitemapShowing() {
      return !!(this.askOptions.length && this.askOptions[0].map)
    },
    // the list only exists while the box has focus AND has matches, so it
    // never hangs around over the page after a click elsewhere
    suggestionsOpen() {
      return this.askFocused && this.askOptions.length > 0
    },
    activeSuggestionId() {
      return this.activeSuggestion >= 0 ? 'fi-tn-ask-opt-' + this.activeSuggestion : null
    },
    // anyone with something to switch between, whenever the bar has room for
    // it. a one-option select is a dead control, and below the rail-hidden
    // breakpoint the rail's drawer copy takes over instead -- fi_section_nav
    // gates on the same expression so exactly one of them renders.
    showTenantSelect() {
      return !this.isMobile && this.tenantOptions.length > 1
    },
    // mirrors the rail's list and navbar visibleTenantOptions (navbar.vue:733):
    // muggle preview narrows to same-fleet tenants by id prefix
    tenantOptions() {
      const all = (this.user && this.user.tenant_options) || []
      if (!this.viewAsNonAdmin) return all
      const current = this.user && this.user.tenant
      if (!current || typeof current !== 'string') return all
      const prefix = current.split('-', 1)[0]
      if (!prefix) return all
      const sameFleet = all.filter(opt => {
        const v = typeof opt === 'object' ? opt.value : opt
        return typeof v === 'string' && v.split('-', 1)[0] === prefix
      })
      return sameFleet.length > 0 ? sameFleet : all
    },
    // which avatar the assistant wears (same fallback chain as the launcher)
    avatarVariant() {
      const p = this.user && this.user.preferences
      const v = p && p.fa_icon
      return (v === 'cloud' || v === 'stapler' || v === 'nova' || v === 'plasma') ? v : 'bubble'
    },
    unreadBadgeText() {
      return this.unreadCount > 9 ? '9+' : String(this.unreadCount)
    },
    viewAsNonAdmin() {
      return this.viewAs === 'non-admin'
    },
    // picker state: unknown stored values render as classic, matching the
    // applyFiTheme coercion in the profile store
    themeChoice() {
      return (this.siteTheme === 'new' || this.siteTheme === 'midnight') ? this.siteTheme : 'original'
    },
    initials() {
      // mirrors navbar userInitials (navbar.vue:734-745): first + last
      // initials from full_name, first letter of the local part for emails
      const name = (this.user && (this.user.full_name || this.user.username)) || ''
      if (name.includes('@')) {
        return name.split('@')[0].charAt(0).toUpperCase()
      }
      const parts = name.trim().split(/\s+/)
      if (parts.length >= 2) {
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
      }
      return name.charAt(0).toUpperCase() || '?'
    },
    displayName() {
      const u = this.user || {}
      return u.full_name || u.username
    },
  },
  watch: {
    $route() {
      this.closeAll()
    },
    // covers the programmatic writes too -- submit retaining the question,
    // fa-closed clearing it, a suggestion being picked -- not just typing
    askText() {
      this.$nextTick(this.autosizeAsk)
    },
    // the list scrolls once it is the site map rather than a few matches, so
    // the arrowed-to row has to be brought back into view or the highlight
    // walks off the fold and the keys look dead. 'nearest' scrolls the list
    // and nothing else when the row is already on screen.
    activeSuggestion(i) {
      if (i < 0) return
      this.$nextTick(() => {
        const el = document.getElementById('fi-tn-ask-opt-' + i)
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
      })
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocClick)
    document.addEventListener('keydown', this.onKeydown)
    // fetches once per page load however many consumers ask (see its own
    // comment); the stars would otherwise paint empty until something else
    // on the page happened to load them
    ensureFavoritesLoaded()
    // same deal for the ask box's history: the typeahead reads a synchronous
    // cache, and this is what fills it with what the user asked on their
    // other devices
    ensureRecentQuestionsLoaded()
    // object-search debounce state: deliberately non-reactive
    this._objTimer = null
    this._objSeq = 0
    this._faUnreadHandler = (v) => { this.faUnread = !!v }
    this.$root.$on('fa-unread-change', this._faUnreadHandler)
    // closing the panel ends the ask the box was mid-flight on
    this._faClosedHandler = () => {
      this.askText = ''
      this.closeSuggestions()
    }
    this.$root.$on('fa-closed', this._faClosedHandler)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocClick)
    document.removeEventListener('keydown', this.onKeydown)
    if (this._objTimer) clearTimeout(this._objTimer)
    if (this._faUnreadHandler) this.$root.$off('fa-unread-change', this._faUnreadHandler)
    if (this._faClosedHandler) this.$root.$off('fa-closed', this._faClosedHandler)
  },
  methods: {
    onToggleNav() {
      // one button, viewport-dependent: drawer on small screens, icon-rail
      // collapse on desktop (mirrors the rail's own breakpoint at 991px)
      if (railHiddenNow()) {
        setMobileOpen(!navState.mobileOpen)
      } else {
        // a drawer left open on a narrow window must not linger as state
        // once the viewport is desktop again
        setMobileOpen(false)
        toggleCollapsed()
      }
    },
    toggleNotifications() {
      this.helpOpen = false
      this.avatarOpen = false
      this.notificationsOpen = !this.notificationsOpen
      if (this.notificationsOpen) {
        // same freshness contract as the navbar dropdown: App.vue owns the
        // 60s poll, this asks it for an immediate refresh on open
        try { this.$root.$emit('nc-refresh') } catch (_) { /* bus not ready */ }
      }
    },
    toggleHelp() {
      this.notificationsOpen = false
      this.avatarOpen = false
      this.helpOpen = !this.helpOpen
    },
    openFa(opts) {
      this.closeAll()
      setMobileOpen(false)
      // the launcher component owns the panel; it listens on $root
      this.$root.$emit('open-fleet-assistant', opts || {})
    },
    copyTenant() {
      copyToClipboard(String(this.user.tenant))
    },
    optValue(opt) {
      return typeof opt === 'object' ? opt.value : opt
    },
    optText(opt) {
      return typeof opt === 'object' ? opt.text : opt
    },
    // same as the rail's handler (fi_section_nav onTenantChange), which in
    // turn mirrors navbar set_tenant_id: persist the choice, and keep an
    // in-route ?tenant= honest so the stale param cannot shadow the switch
    onTenantChange() {
      localStorage.setItem('selected_tenant', this.user.tenant)
      const q = this.$route.query
      if (q && q.tenant && q.tenant !== this.user.tenant) {
        const next = { ...q, tenant: this.user.tenant }
        delete next.event_id
        this.$router.replace({ query: next }).catch(() => {})
      }
    },
    onAskFocus() {
      this.askFocused = true
      // suggestions show the moment the box is entered rather than waiting for
      // a keystroke: an empty box leads with the recent questions and a box
      // holding an unsent draft leads with its matches. a box with NEITHER
      // opens onto nothing, deliberately -- the full site map used to stand in
      // here, and on a tenant with the whole reports package that is a
      // multi-column wall of dozens of rows landing on a single click into a
      // search field. down-arrow still reaches the map for anyone who wants it
      // (moveSuggestion), and the sidebar and site map page are the other two
      // ways in.
      this.onAskInput()
      // the bar's popovers would sit over the panel the box may open
      this.closeAll()
    },
    onAskBlur() {
      this.askFocused = false
      this.closeSuggestions()
    },
    blurAsk() {
      this.askFocused = false
      this.closeSuggestions()
      const el = this.$refs.ask
      if (el && typeof el.blur === 'function') el.blur()
    },
    // ---- typeahead --------------------------------------------------------
    onAskInput() {
      this.suggestions = matchRecents(this.askText)
      this.pageMatches = searchPages(this.user, this.viewAs, this.askText)
      this.activeSuggestion = -1
      // the map never opens on its own, whether the box is empty or holds a
      // query that matched nothing. it is reached by a deliberate down-arrow
      // (moveSuggestion), which is also what keeps a 40-row panel from
      // flashing up between keystrokes.
      this.sitemapOpen = false
      this.queueObjectSearch()
    },
    // the map stands in whenever the list would otherwise be empty. checked
    // AFTER the matches are in, because a real match always wins.
    showSitemapIfEmpty() {
      if (!this.askOptions.length) this.sitemapOpen = true
    },
    expandSection(section) {
      if (!section || this.expandedSections.indexOf(section) !== -1) return
      this.expandedSections = this.expandedSections.concat(section)
    },
    // ---- favorites --------------------------------------------------------
    // stars ride the site map panel only: the typeahead's own rows sit in a
    // 390px well beside a hint, with no room for one, and the map is where
    // browsing (and so starring) actually happens.
    favDestination(o) {
      if (!o || !o.map || o.type !== 'page' || !o.page) return null
      // null for a locked, external or route-less page -- nothing a favorite
      // could point at, so no star rather than a dead one
      return favoritableDestination(o.page)
    },
    canFavoriteRow(o) {
      return !!this.favDestination(o)
    },
    isRowFavorite(o) {
      const dest = this.favDestination(o)
      // touch favState.keys so vue tracks the dependency through isFavorite
      return !!dest && this.favState.keys.length >= 0 && isFavorite(dest)
    },
    toggleRowFavorite(o) {
      const dest = this.favDestination(o)
      if (dest) toggleFavorite(dest)
    },
    // object results come from the api, so they ride a debounce: recents and
    // pages answer every keystroke locally, objects fill in ~a beat later.
    // the sequence counter drops any response that arrives after a newer
    // query was issued, so fast typing can never paint stale results.
    queueObjectSearch() {
      if (this._objTimer) clearTimeout(this._objTimer)
      const q = (this.askText || '').trim()
      if (q.length < 2) {
        this.objectMatches = []
        return
      }
      this._objTimer = setTimeout(async () => {
        const seq = ++this._objSeq
        try {
          const { value } = await search_objects(this.user.tenant, q)
          if (seq !== this._objSeq) return
          // ignore a response for a box that has since been cleared or blurred
          if ((this.askText || '').trim().length < 2) return
          this.objectMatches = (value && value.data && value.data.results) || []
        } catch (e) {
          if (seq === this._objSeq) this.objectMatches = []
        }
      }, 275)
    },
    // grow the box to fit what has been typed, up to ASK_MAX_ROWS, then let it
    // scroll. height is reset to auto first so it can shrink again --
    // scrollHeight otherwise sticks at its previous tall value.
    autosizeAsk() {
      const ta = this.$refs.ask
      if (!ta) return
      ta.style.height = 'auto'
      const cs = window.getComputedStyle(ta)
      const line = parseFloat(cs.lineHeight) || ASK_LINE_HEIGHT
      const max = line * ASK_MAX_ROWS
      ta.style.height = Math.min(ta.scrollHeight, max) + 'px'
      ta.style.overflowY = ta.scrollHeight > max ? 'auto' : 'hidden'
    },
    closeSuggestions() {
      this.suggestions = []
      this.pageMatches = []
      this.objectMatches = []
      if (this._objTimer) clearTimeout(this._objTimer)
      // a fetch already in flight must not repopulate a closed list
      this._objSeq++
      this.sitemapOpen = false
      this.expandedSections = []
      this.activeSuggestion = -1
    },
    // arrows walk the list and stop at both ends rather than wrapping, so
    // holding a key cannot cycle forever. -1 is a real position: the typed
    // text itself, which is what up-arrow returns to from the first row.
    moveSuggestion(step) {
      // down on a closed list opens it and takes the first row. with an empty
      // box that is the plain most-recent list, which is otherwise
      // unreachable -- matches only ever appeared while typing. with nothing
      // to show at all (no recents, nothing typed or nothing matching it) the
      // site map overview stands in, so down always opens SOMETHING.
      if (!this.suggestionsOpen) {
        if (step < 0) return
        this.onAskInput()
        // unconditional here, unlike onAskInput: down on a typed query that
        // matched nothing is a deliberate ask for somewhere to go
        this.showSitemapIfEmpty()
        this.activeSuggestion = this.askOptions.length ? 0 : -1
        return
      }
      const last = this.askOptions.length - 1
      const next = this.activeSuggestion + step
      this.activeSuggestion = Math.max(-1, Math.min(last, next))
    },
    chooseSuggestion(i) {
      const opt = this.askOptions[i]
      if (!opt) return
      // the only row that acts on the LIST rather than leaving it. the
      // highlight is left where it is, which after the rows arrive is the
      // first of the newly revealed ones -- exactly where the eye already is.
      if (opt.type === 'more') {
        this.expandSection(opt.section)
        return
      }
      if (opt.type === 'page') {
        this.goToPage(opt.page)
        return
      }
      if (opt.type === 'object') {
        this.goToObject(opt.object)
        return
      }
      // a remembered destination replays its stored target directly: the page
      // row or api object it came from is long gone, which is the whole
      // reason the resolved destination is what gets stored
      if (opt.type === 'recent_destination') {
        this.goToRecent(opt.recent)
        return
      }
      this.askText = opt.label
      this.closeSuggestions()
      this.submitAsk()
    },
    // a page row is navigation, not a question: the assistant is never
    // involved, and the box is cleared because the text was a search for the
    // page, not something worth keeping as the "current question"
    goToPage(page) {
      if (!page) return
      this.askText = ''
      this.closeSuggestions()
      this.blurAsk()
      if (page.external && page.path) {
        rememberDestination({
          label: page.name, kind: 'page', icon: page.icon,
          to: { path: page.path }, external: true,
        })
        window.open(page.path, '_blank', 'noopener')
        return
      }
      const to = page.route
        ? { name: page.route, query: page.query || undefined }
        : { path: page.path }
      rememberDestination({ label: page.name, kind: 'page', icon: page.icon, to })
      // push to the CURRENT route rejects; harmless, and not worth surfacing
      this.$router.push(to).catch(() => {})
    },
    // replay of a remembered destination. same two paths as goToPage, minus
    // the resolution, because the target was resolved when it was recorded.
    goToRecent(entry) {
      if (!entry || !entry.to) return
      this.askText = ''
      this.closeSuggestions()
      this.blurAsk()
      // re-record so picking it again moves it back up the list
      rememberDestination({
        label: entry.q, kind: entry.kind, icon: entry.icon,
        to: entry.to, external: entry.external,
      })
      if (entry.external && entry.to.path) {
        window.open(entry.to.path, '_blank', 'noopener')
        return
      }
      this.$router.push(entry.to).catch(() => {})
    },
    // an object row navigates to the page that owns that entity. bookings
    // and ops events land FOCUSED on the picked record (both pages read the
    // query param); drivers, vehicles, and users land on their home page,
    // which is as deep as those pages can currently link.
    goToObject(object) {
      if (!object) return
      this.askText = ''
      this.closeSuggestions()
      this.blurAsk()
      const tbn = this.ctx.tenantType === 'TBN'
      const routes = {
        driver: () => ({ name: tbn ? 'driver_list_tbn' : 'driver_list' }),
        // map_fleet, not legacy /vehicle_map: the standalone route is only the
        // dattco-driver whitelist destination and errors outside the maps shell
        vehicle: () => ({ name: 'map_fleet' }),
        booking: () => ({ name: 'actuals', query: { booking_id: object.id } }),
        user: () => ({ name: 'user_detail', query: { username: object.id } }),
        ops_event: () => ({ name: 'ops_desk', query: { event_id: object.id } }),
      }
      const to = routes[object.type] && routes[object.type]()
      if (!to) return
      // the label carries the type, because "Kirk Young" and a booking id read
      // very differently in a bare Recent list a day later
      const meta = {
        driver: 'fa-user-o', vehicle: 'fa-bus', booking: 'fa-calendar-check-o',
        user: 'fa-users', ops_event: 'fa-clipboard',
      }
      rememberDestination({
        label: object.label || object.id, kind: 'object',
        icon: meta[object.type], to,
      })
      this.$router.push(to).catch(() => {})
    },
    // esc backs out one step at a time: first the suggestion list, then the
    // box itself
    onAskEscape() {
      if (this.suggestionsOpen) {
        this.closeSuggestions()
        return
      }
      this.blurAsk()
    },
    // enter expands the box into the assistant: the panel drops out of it and
    // the question is sent straight away -- exactly as if it had been typed
    // into the assistant and Send clicked. the box behaves like a search
    // field rather than a second chat input, so typing alone does nothing and
    // the panel comes up as a RESULT view (searchMode: no composer at its
    // foot, no earlier turns). an empty box gets the ordinary chat panel,
    // which is what clicking into the box and pressing enter amounts to.
    submitAsk() {
      // an arrowed-to suggestion wins over what was typed; with none active
      // (-1) the typed text stands, so enter never silently swaps the question
      // enter on a highlighted PAGE navigates instead of asking -- same as
      // clicking it, so the highlight means the same thing either way
      const active = this.activeSuggestion >= 0 ? this.askOptions[this.activeSuggestion] : null
      // enter on a highlighted Show more expands it, same as clicking it --
      // the box is not asked anything and the list stays up
      if (active && active.type === 'more') {
        this.expandSection(active.section)
        return
      }
      if (active && active.type === 'page') {
        this.goToPage(active.page)
        return
      }
      if (active && active.type === 'object') {
        this.goToObject(active.object)
        return
      }
      const picked = active ? active.label : ''
      const q = (picked || this.askText || '').trim()
      // the panel opens as the full assistant (session docs, new chat, prior
      // turns) and echoes the question as its own chat bubble, so the box
      // hands the text off and clears instead of holding a duplicate copy
      this.askText = ''
      this.blurAsk()
      const opts = { anchor: this.askAnchor() }
      if (q) {
        opts.initialQuery = q
        opts.autoSend = true
      }
      this.openFa(opts)
    },
    // the viewport rect the panel drops out of; null if the box isn't
    // rendered (mobile), in which case the panel opens where it last was
    askAnchor() {
      const el = this.$refs.askForm
      if (!el || typeof el.getBoundingClientRect !== 'function') return null
      const r = el.getBoundingClientRect()
      return { bottom: r.bottom, right: r.right }
    },
    toggleAvatar() {
      this.notificationsOpen = false
      this.helpOpen = false
      this.avatarOpen = !this.avatarOpen
      if (this.avatarOpen && this.isMobile) {
        // the account panel shows notifications and sits under the drawer's
        // z-index, so the drawer must not stay open over it
        setMobileOpen(false)
        try { this.$root.$emit('nc-refresh') } catch (_) { /* bus not ready */ }
      }
    },
    closeAll() {
      this.notificationsOpen = false
      this.helpOpen = false
      this.avatarOpen = false
    },
    // hover tooltips for the bar icons, same treatment as the rail rows
    // (fi_section_nav rowTip); .fi-sn-tip is unscoped there so it applies.
    // suppressed on touch devices: a tap emulates hover and the tip sticks
    // open over the popover it just opened (chaz round 12)
    barTip(text) {
      if (noHoverDevice()) return { title: '' }
      return { title: text || '', delay: { show: 450, hide: 0 }, boundary: 'window', customClass: 'fi-sn-tip' }
    },
    onDocClick() {
      this.closeAll()
    },
    onKeydown(e) {
      if (e.key === 'Escape') this.closeAll()
    },
    // viewer sees their own role, so the internal registry variant applies
    // (staff read "SYS ADMIN"/shield), mirroring navbar.vue:525-530
    roleIconFor(role) {
      return roleIconForInternal(role)
    },
    roleDisplayLabel(role) {
      return roleLabelForInternal(role)
    },
    toggleViewAs() {
      const next = this.viewAsNonAdmin ? null : 'non-admin'
      this.$store.commit('profile/set_view_as', next)
    },
    async setSiteTheme(next) {
      if (next === this.siteTheme) return
      const prev = this.siteTheme
      this.$store.commit('profile/set_site_theme', next)
      try {
        await update_site_theme(next)
      } catch (e) {
        this.$store.commit('profile/set_site_theme', prev)
      }
    },
    logout() {
      this.closeAll()
      this.$router.push('/login')
      this.$emit('authenticated', false)
    },
  },
}
</script>

<style scoped>
.fi-tn {
  /* fixed, not sticky: #app's overflow-x fallback (App.vue) can break
     sticky, and the rail's wrapper gives sticky no room anyway */
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1030;
}
.fi-tn-toggle-glyph {
  display: block;
  width: 20px;
  height: 16px;
}
.fi-tn-bar {
  /* positioning context for the mobile centered brand lockup */
  position: relative;
  display: flex;
  align-items: center;
  /* ONE gap for the whole bar, and the right-hand controls carry no margins of
     their own on top of it -- they used to, unevenly, so the run from the
     switcher to the avatar read 36 / 12 / 22 / 22px and looked accidental.
     10px everywhere is uniform and a touch tighter than the tightest of those.
     the left side spaces itself instead (the lockup's and the ask box's own
     margins), which is why loosening there did not have to loosen here.
     vertical: the bar is a fixed 52px around 36px controls, so the padding
     above and below the content is 8px -- the same 8px .fi-fb (the favorites
     toolbar) sets explicitly. keep the two in step if either changes. */
  gap: 10px;
  height: 52px;
  padding: 0 16px 0 6px;
  /* solid classic steelblue (no backdrop blur: the token went opaque, so a
     filter would only cost compositing) */
  background: var(--fi-topbar-bg);
  border-bottom: 1px solid var(--fi-topbar-line);
}
.fi-tn-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: none;
  background: transparent;
  color: #fff;
  font-size: 17px;
  border-radius: var(--fi-radius-sm);
  cursor: pointer;
  transition: background-color var(--fi-motion-quick);
}
.fi-tn-toggle:hover { background: rgba(255, 255, 255, 0.16); }
.fi-tn-brand {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-left: 8px;
  white-space: nowrap;
  /* the ask box sits immediately right of the lockup now, so the lockup has to
     hold its width rather than shrink to make room for it */
  flex: 0 0 auto;
}
/* sized against the right cluster (36px avatar, 28px help glyph): the mark
   reads ~avatar-height and the wordmark ~the bar's visual midweight */
.fi-tn-brand-logo {
  display: block;
  width: 36px;
  height: 23px;
  color: #fff;
}
.fi-tn-brand-name {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
}
/* tenant switcher: quieter than the ask box (an outlined rect, not that
   control's filled pill -- two adjacent frosted pills read as one confusing
   double control) but outlined all the same, so it reads as openable at rest
   rather than as a line of bar text. white type, a small chevron, and a fill
   that arrives on hover. sits immediately left of the notifications bell, at
   the head of the right-hand cluster. */
.fi-tn-tenant {
  display: flex;
  align-items: center;
  gap: 6px;
  /* no margin of its own: the bar's gap is the one spacing in the right-hand
     cluster, so the switcher sits the same distance from the support glyph as
     every other pair does */
  min-width: 0;
}
.fi-tn-tenant-well {
  position: relative;
  display: inline-flex;
  min-width: 0;
}
/* the chevron is drawn here because a native select cannot style its own
   arrow cross-browser; appearance:none below removes the platform one */
.fi-tn-tenant-well::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 10px;
  width: 7px;
  height: 7px;
  border-right: 2px solid rgba(255, 255, 255, 0.8);
  border-bottom: 2px solid rgba(255, 255, 255, 0.8);
  transform: translateY(-70%) rotate(45deg);
  pointer-events: none;
}
/* bare glyph, matching the bell/help treatment rather than the select's well
   -- it is an action on the selection, not part of the control */
/* no glyph, but a real hit area: an empty button collapses to zero width, so
   the size is set explicitly. matched to the select's height so the strip
   beside the dropdown is uniformly clickable. */
.fi-tn-tenant-copy {
  display: inline-block;
  width: 20px;
  height: 36px;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: var(--fi-radius-sm);
  cursor: pointer;
}
.fi-tn-tenant-copy:focus { outline: none; box-shadow: none; }
.fi-tn-tenant-copy:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.45);
}
.fi-tn-tenant-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  /* 180 not 220: a native select sizes to its LONGEST option, so an admin's
     hundred-tenant list always hit the old cap and crowded the centred ask
     box. long names ellipsize; the popup list shows them in full. */
  max-width: 180px;
  /* same 36px as the ask box and the account avatar, so every control in the
     bar sits on one optical line */
  height: 36px;
  padding: 0 26px 0 10px;
  /* the same hairline the ask box wears, so the two read as one family of
     controls and the switcher looks like something you can open even before
     it is hovered -- the hover well alone left it looking like plain text */
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: var(--fi-radius-sm);
  background: transparent;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color var(--fi-motion-quick), border-color var(--fi-motion-quick);
}
.fi-tn-tenant-select:hover {
  background: rgba(255, 255, 255, 0.16);
  border-color: rgba(255, 255, 255, 0.55);
}
.fi-tn-tenant-select:focus { outline: none; box-shadow: none; }
.fi-tn-tenant-select:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.45);
}
/* the popup list is OS-drawn: white-on-translucent would be unreadable there,
   so the options get normal surface colors back */
.fi-tn-tenant-select option {
  color: #212529;
  background: #fff;
}
.fi-tn-spacer { flex: 1 1 auto; }
/* desktop ask box: a frosted well on the steelblue bar, sized to sit between
   the brand and the right-hand chrome cluster. it grows toward the brand on
   focus rather than pushing the chrome around, so the icons never shift under
   the pointer. white-on-translucent matches the bar's other controls; the
   search input's native decorations are stripped so it reads as one well. */
/* left-aligned, immediately right of the brand lockup: the box is the bar's
   primary control, and reading order puts it with the identity it belongs to
   rather than floating mid-bar. it rides the flex flow now (the spacer that
   follows pushes the chrome cluster right), which is also what keeps it from
   ever overlapping that cluster -- the old centred box was absolute and needed
   a hand-tuned side reserve to stay clear of it. still the positioning context
   for the typeahead list, which hangs below the well. */
.fi-tn-ask-wrap {
  position: relative;
  /* opted OUT of the bar's centring on purpose: a centred item grows in both
     directions, so a wrapped question expanded equally upward, off the top of
     the fixed bar, and looked chopped. pinned to the top instead, every extra
     row extends downward, past the bar and over the page.
     the margin centres the RESTING box in the 52px bar: the box rests at 36px
     (matching the 36px account avatar, the tallest thing in the right-hand
     cluster), so (52-36)/2 = 8. keep this in step with .fi-tn-ask's
     min-height -- an offset derived from the wrong height is what left the
     box sitting low before. */
  align-self: flex-start;
  margin: 8px 0 0 18px;
  /* the wrap shrink-wraps the well (the typeahead list pins to the wrap's
     edges, so the two must stay the same width) and gives way before the
     lockup or the chrome do if the bar runs out of room */
  width: max-content;
  flex: 0 1 auto;
  min-width: 0;
  /* the well grows downward past the bar's 52px as the question wraps, so it
     has to paint over page content. below the typeahead list's 2002 -- the
     list still hangs under the grown box. */
  z-index: 2001;
}
.fi-tn-ask {
  display: flex;
  align-items: center;
  gap: 8px;
  /* resting width is 25% under the focused one below, so the box is quiet
     until it is being used and the expansion on focus is visible */
  width: 390px;
  /* the wrap above carries the real clamp (bar-relative, scrollbar-proof);
     this only keeps the well from outgrowing the wrap the list is pinned to */
  max-width: 100%;
  /* min, not fixed: the textarea inside grows as the question wraps and the
     well grows with it, downward past the bar (see the wrap's z-index).
     36px matches the account avatar, the tallest control in the bar's right
     cluster, so the well sits on the same optical line as the chrome rather
     than looming over it. the 22px glyph inside plus 6px padding each side
     and 1px borders lands exactly there; keep the wrap's centring offset in
     step if this changes. */
  min-height: 36px;
  padding: 6px 12px 6px 10px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 999px;
  /* the tint is layered over an OPAQUE base rather than used alone. on the bar
     the two are indistinguishable -- the base is the bar's own colour -- but
     once a wrapped question grows the box past the bar, a translucent fill
     would let the light page through and white text on near-white read as if
     the bottom rows had been cut off. */
  background: linear-gradient(rgba(255, 255, 255, 0.14), rgba(255, 255, 255, 0.14)), var(--fi-topbar-bg);
  transition: width var(--fi-motion-quick), background-color var(--fi-motion-quick),
    border-color var(--fi-motion-quick), box-shadow var(--fi-motion-quick);
}
.fi-tn-ask:hover { background: linear-gradient(rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.2)), var(--fi-topbar-bg); }
.fi-tn-ask--focused {
  width: 570px;
  background: linear-gradient(rgba(255, 255, 255, 0.24), rgba(255, 255, 255, 0.24)), var(--fi-topbar-bg);
  border-color: rgba(255, 255, 255, 0.55);
  /* the ring reads on the bar; the drop shadow is what makes the part hanging
     BELOW the bar read as floating over the page rather than stuck to it */
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.16), 0 6px 16px rgba(0, 0, 0, 0.28);
}
/* the avatar carries the unread attention animation, same as the pill did */
.fi-tn-ask-glyph {
  flex: 0 0 auto;
  display: block;
  width: 22px;
  height: 22px;
  color: rgba(255, 255, 255, 0.9);
}
.fi-tn-ask-input {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  background: transparent;
  color: #fff;
  font-size: 14px;
  /* the js autosize measures against this; keep the two in step
     (ASK_LINE_HEIGHT) or the box grows by the wrong step */
  line-height: 18px;
  /* one row to start, grown by autosizeAsk. no manual resize grip, and the
     scrollbar only appears once the row cap is hit. */
  resize: none;
  overflow-y: hidden;
  height: 18px;
  padding: 0;
  font-family: inherit;
  -webkit-appearance: none;
  appearance: none;
}
.fi-tn-ask-input::placeholder { color: rgba(255, 255, 255, 0.7); }
.fi-tn-ask-input:focus { outline: none; box-shadow: none; }
/* typeahead list: the same glass popover treatment as the bar's help and
   account menus, so it themes with them (incl. midnight) instead of the
   well's translucent white-on-blue, which long question text can't sit on.
   right-aligned to the well so it stays put as the well widens on focus. */
.fi-tn-ask-list {
  position: absolute;
  top: calc(100% + 6px);
  /* both edges pinned: the list spans the well exactly, at whatever width the
     focus state has it, instead of carrying its own fixed width */
  left: 0;
  right: 0;
  z-index: 2002;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  text-align: left;
  /* more rows than fit the window scroll inside the list rather than running
     off the bottom of it. arrow-keying past the fold scrolls the active row
     into view (see the activeSuggestion watcher), and the containment keeps a
     flick at the end of the list off the page behind it. */
  max-height: 60vh;
  overflow-y: auto;
  overscroll-behavior: contain;
  border-radius: var(--fi-radius-md);
  background: var(--fi-glass-bg-strong);
  -webkit-backdrop-filter: blur(var(--fi-glass-blur)) saturate(var(--fi-glass-saturate));
  backdrop-filter: blur(var(--fi-glass-blur)) saturate(var(--fi-glass-saturate));
  border: 1px solid var(--fi-glass-line);
  box-shadow: var(--fi-glass-shadow);
}
.fi-tn-ask-group { display: block; }
/* in the map's columns a section is an unbreakable block: a heading stranded
   at the foot of one column with its pages in the next is worse than an
   uneven column. -webkit- for safari, which still wants the old property. */
.fi-tn-ask-list--map .fi-tn-ask-group {
  -webkit-column-break-inside: avoid;
  break-inside: avoid;
  /* the balancer measures margins, so this both separates the sections and
     keeps the columns honest */
  margin-bottom: 4px;
}
/* the site map fallback: every section and every page under it -- a 40-row
   strip stacked, a readable board in columns. so this one state breaks out of
   the well's width. it stays pinned to the well's left edge and grows
   rightward across the bar it drops out of, which is what the box being
   left-aligned buys: a centred box had no room on either side to do this. */
.fi-tn-ask-list--map {
  /* left stays pinned to the well; the right edge is released so the width
     below can take effect -- both edges pinned would ignore it */
  right: auto;
  /* the viewport allowance covers the bar's left run-up (brand lockup plus the
     box's own offset), so the panel never runs off the right edge on a narrow
     desktop. never narrower than the well it hangs from. */
  width: calc(100vw - 300px);
  max-width: 880px;
  /* min beats max in the cascade, so the panel is never narrower than the well
     it hangs from, however tight the window gets */
  min-width: 100%;
  /* multi-column rather than grid: the sections are wildly different lengths
     (Maps has three pages, Reports fifteen), and a grid puts them in ROWS, so
     every short section left a hole the height of the tallest one beside it.
     columns pack them end to end down each column instead and the browser
     balances the heights -- the panel comes out roughly as tall as its longest
     single section instead of the sum of the tallest in each row.
     column-width, not column-count: four columns on a wide screen folding to
     two on a narrow one, without a media query per step. 210px is where the
     longest page names still sit on one line. */
  -webkit-columns: 210px;
  -moz-columns: 210px;
  columns: 210px;
  column-gap: 16px;
  padding: 10px 6px 12px;
  /* no height cap here, deliberately: a multi-column box with one would spill
     into further columns SIDEWAYS rather than scroll, which is why the base
     rule's max-height/overflow pair is overridden away. balancing is what
     keeps it short -- the whole map is ~48 rows over four columns. */
  max-height: none;
  overflow: visible;
}
/* tighter rhythm than the stacked list: at forty rows the typeahead's spacing
   reads as a wall */
.fi-tn-ask-list--map .fi-tn-ask-opt {
  padding: 4px 10px;
  border-radius: var(--fi-radius-sm);
}
.fi-tn-ask-list--map .fi-tn-ask-hd { padding: 9px 10px 3px; }
.fi-tn-ask-opt {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 14px;
  color: var(--fi-chrome-ink);
  font-size: 13.5px;
  cursor: pointer;
  transition: background-color var(--fi-motion-quick);
}
/* star: quiet until the row is under the pointer or the arrow keys, and always
   lit once it is ON, so forty rows do not read as forty outlined stars. it
   keeps its slot in the row at all times (opacity, not display) -- appearing
   on hover would otherwise squeeze the label and shift the text under the
   pointer. --fi-chrome-star is the same lit colour the site map tiles use. */
.fi-tn-ask-star {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-left: 6px;
  padding: 0;
  border: none;
  border-radius: var(--fi-radius-sm);
  background: transparent;
  color: var(--fi-chrome-muted);
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  opacity: 0;
  transition: opacity var(--fi-motion-quick), color var(--fi-motion-quick);
}
.fi-tn-ask-opt:hover .fi-tn-ask-star,
.fi-tn-ask-opt--active .fi-tn-ask-star { opacity: 1; }
.fi-tn-ask-star:hover { color: var(--fi-chrome-star); }
.fi-tn-ask-star--on,
.fi-tn-ask-star--on:hover {
  color: var(--fi-chrome-star);
  opacity: 1;
}
.fi-tn-ask-star:focus { outline: none; box-shadow: none; }
.fi-tn-ask-star:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--fi-action-primary-ring);
}
/* the one row that acts on the list instead of leaving it, so it is drawn as
   a quiet control rather than another destination: muted, lighter, and its
   glyph carries none of the page rows' meaning */
.fi-tn-ask-opt--more {
  color: var(--fi-chrome-eyebrow);
  font-size: 12.5px;
  font-weight: 600;
}
.fi-tn-ask-opt--active {
  background: var(--fi-chrome-hover-bg);
  color: var(--fi-chrome-ink-strong);
}
.fi-tn-ask-opt-icon {
  flex: 0 0 auto;
  width: 14px;
  text-align: center;
  opacity: 0.6;
  font-size: 12px;
}
/* section heading: not a role=option, so arrow keys skip straight past it */
.fi-tn-ask-hd {
  padding: 6px 14px 3px;
  color: var(--fi-chrome-eyebrow);
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
/* which section of the app the page lives in, so two similarly named pages
   are tellable apart */
.fi-tn-ask-opt-hint {
  flex: 0 0 auto;
  margin-left: auto;
  padding-left: 10px;
  opacity: 0.6;
  font-size: 11.5px;
  white-space: nowrap;
}
/* one line per question; the full text is the title on hover */
.fi-tn-ask-opt-text {
  flex: 1 1 auto;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
/* mid-widths (rail visible but bar tight): shrink the resting well so the
   brand wordmark and the chrome cluster both keep their space */
@media (max-width: 1200px) {
  .fi-tn-ask { width: 285px; }
  .fi-tn-ask--focused { width: 450px; }
}
.fi-tn-bell-wrap,
.fi-tn-help-wrap,
.fi-tn-avatar-wrap { position: relative; }
/* desktop: widen the right-side chrome cluster (notifications / help /
   account) so the icons breathe like the classic navbar's right group
   instead of crowding at the bar's 8px gap. the mobile bar hides the bell
   and drops help, so it keeps the tight default and is left untouched. */
/* the desktop cluster used to widen itself here with a per-control margin-left
   on top of the bar's gap, which is what left the bell sitting closer to the
   support glyph than the help glyph sat to either neighbour. the bar's single
   gap now spaces all of them, so there is nothing to add. */
/* classic icon treatment (navbar.vue .nav-icon-btn): bare 28px glyph, no
   hover pill -- the glyph just brightens */
.fi-tn-bell,
.fi-tn-support,
.fi-tn-help {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 4px;
  border: none;
  background: transparent;
  border-radius: var(--fi-radius-sm);
  color: rgba(255, 255, 255, 0.85);
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  transition: color 0.15s;
}
.fi-tn-bell:hover,
.fi-tn-help:hover { color: #fff; }
/* !important + text-decoration: it is an anchor, and App.vue's globals paint
   bare anchors steelblue and underline them on hover.
   fa-ticket is a short, wide diagonal glyph -- its ink is nowhere near the
   bell's ~1.02em height, so it keeps the shared 28px rather than the bell's
   reduced size and still reads no taller than its neighbors.
   wizard purple: the glyph is staff-only (isEffectiveAdmin), so it wears the
   admin cue like the rail's staff rows. hardcoded dark-lifted value, not
   var(--fi-chrome-admin): the bar is dark in every theme, and the token
   resolves to a light-surface purple outside midnight that would vanish
   here. #cfa4e8 is midnight's own --fi-chrome-admin. */
.fi-tn-support {
  color: #cfa4e8 !important;
  text-decoration: none;
}
.fi-tn-support:hover { color: #e7d3f7 !important; text-decoration: none; }
/* optical size correction: fa-bell-o ink spans ~1.02em vs fa-question-circle's
   ~0.88em circle, so at a shared 28px the bell reads taller. 24px lands both
   glyphs at the same visual height, diminutive to the 36px avatar. */
.fi-tn-bell { font-size: 24px; }
/* mobile fa button: same bare-glyph treatment as bell/help. the bubble and
   cloud avatar variants draw in currentColor (white here); the art variants
   carry their own colors, matching how they render everywhere else */
.fi-tn-fa {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 4px;
  border: none;
  background: transparent;
  border-radius: var(--fi-radius-sm);
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  transition: color 0.15s;
}
.fi-tn-fa:hover { color: #fff; }
.fi-tn-fa-glyph {
  display: block;
  width: 26px;
  height: 26px;
}
/* bare wrapper: the circle itself is navbar's global .profile-avatar */
.fi-tn-avatar-btn {
  display: inline-flex;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
}
/* classic hover (navbar.vue .nav-icon-btn .nav-link:hover .profile-avatar) */
.fi-tn-avatar-btn:hover .profile-avatar { background: rgba(255, 255, 255, 0.3); }
/* bootstrap's reboot draws a focus ring on plain :focus, which lingers after
   a mouse click; keep the ring for keyboard focus only */
.fi-tn-toggle:focus,
.fi-tn-bell:focus,
.fi-tn-fa:focus,
.fi-tn-support:focus,
.fi-tn-help:focus,
.fi-tn-avatar-btn:focus,
.fi-tn-menu-item:focus {
  outline: none;
  box-shadow: none;
}
.fi-tn-toggle:focus-visible,
.fi-tn-bell:focus-visible,
.fi-tn-fa:focus-visible,
.fi-tn-support:focus-visible,
.fi-tn-help:focus-visible,
.fi-tn-avatar-btn:focus-visible,
.fi-tn-menu-item:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.45);
}
.fi-tn-nc-pop {
  position: absolute;
  top: 46px;
  right: 0;
  width: 400px;
  max-width: calc(100vw - 20px);
  /* App.vue's .container-fluid centers text app-wide; cards read left-aligned
     (the navbar variant got this for free from bootstrap's .dropdown-menu) */
  text-align: left;
  max-height: calc(100vh - 70px);
  overflow-y: auto;
  padding: 4px 2px 8px;
  border-radius: var(--fi-radius-lg);
  /* strong tier, not clear: notification cards are dense text over whatever
     page is behind them -- readability beats the glass effect here */
  background: var(--fi-glass-bg-strong);
  -webkit-backdrop-filter: blur(var(--fi-glass-blur)) saturate(var(--fi-glass-saturate));
  backdrop-filter: blur(var(--fi-glass-blur)) saturate(var(--fi-glass-saturate));
  border: 1px solid var(--fi-glass-line);
  box-shadow: var(--fi-glass-shadow);
  z-index: 2002;
}
.fi-tn-nc-pop .nc-msg-footer { padding: 8px 10px 2px; }
.fi-tn-help-pop {
  position: absolute;
  top: 46px;
  right: 0;
  width: 240px;
  max-width: calc(100vw - 20px);
  text-align: left;
  padding: 6px 0;
  border-radius: var(--fi-radius-md);
  background: var(--fi-glass-bg-strong);
  -webkit-backdrop-filter: blur(var(--fi-glass-blur)) saturate(var(--fi-glass-saturate));
  backdrop-filter: blur(var(--fi-glass-blur)) saturate(var(--fi-glass-saturate));
  border: 1px solid var(--fi-glass-line);
  box-shadow: var(--fi-glass-shadow);
  z-index: 2002;
}
.fi-tn-profile-pop {
  position: absolute;
  top: 46px;
  right: 0;
  /* wide enough that a typical name + role tag share the top row like the
     legacy dropdown */
  width: 340px;
  max-width: calc(100vw - 20px);
  text-align: left;
  padding: 8px 0;
  border-radius: var(--fi-radius-md);
  background: var(--fi-glass-bg-strong);
  -webkit-backdrop-filter: blur(var(--fi-glass-blur)) saturate(var(--fi-glass-saturate));
  backdrop-filter: blur(var(--fi-glass-blur)) saturate(var(--fi-glass-saturate));
  border: 1px solid var(--fi-glass-line);
  box-shadow: var(--fi-glass-shadow);
  z-index: 2002;
}
.fi-tn-menu-label {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fi-chrome-eyebrow);
  padding: 6px 14px 3px;
}
.fi-tn-menu-item {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 14px;
  /* !important: App.vue globals force steelblue on bare anchors */
  color: var(--fi-chrome-ink) !important;
  font-size: 13.5px;
  text-decoration: none;
  transition: background-color var(--fi-motion-quick);
}
.fi-tn-menu-item:hover {
  background: var(--fi-chrome-hover-bg);
  color: var(--fi-chrome-ink-strong) !important;
  text-decoration: none;
}
.fi-tn-menu-icon {
  flex: 0 0 auto;
  width: 17px;
  text-align: center;
  font-size: 14px;
  color: var(--fi-chrome-muted);
}
.fi-tn-menu-ext {
  margin-left: auto;
  font-size: 10px;
  opacity: 0.6;
}
/* the profile/logout rows reuse the menu-item look; the button variant
   needs the native chrome stripped. no `font` shorthand here: it would
   out-specify the class's 13.5px font-size and render logout larger than
   its sibling link rows (reboot already inherits the family on buttons) */
button.fi-tn-menu-item {
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
}
.fi-tn-menu-item--danger,
.fi-tn-menu-item--danger .fi-tn-menu-icon { color: var(--fi-chrome-danger) !important; }
.fi-tn-menu-item--danger:hover { background: var(--fi-chrome-danger-hover-bg); }
/* same drawing as bootstrap's .dropdown-divider between menu groups */
.fi-tn-pop-divider {
  height: 0;
  margin: 8px 0;
  border-top: 1px solid var(--fi-chrome-divider);
}
/* the account panel is a page, not a popover: opaque, full width, scrolls */
.fi-tn-account-panel {
  position: fixed;
  top: 52px;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  background: var(--fi-chrome-sheet-bg);
  text-align: left;
  padding: 4px 0 24px;
}
/* midnight: the identity card + theme picker reuse navbar's global
   .profile-* rules, which are inked for a light popover. re-ink them for
   the dark chrome here (classic never runs under midnight, and these are
   scoped to this component's template, so nothing leaks to the classic
   dropdown). the role/muggle chips keep their own light fills and stay. */
:root[data-fi-theme="midnight"] .profile-dropdown-header { background: transparent; }
:root[data-fi-theme="midnight"] .profile-display-name { color: var(--fi-chrome-ink-strong); }
:root[data-fi-theme="midnight"] .profile-email { color: var(--fi-chrome-muted); }
:root[data-fi-theme="midnight"] .profile-dd-avatar {
  border-color: rgba(111, 177, 232, 0.45);
  background: rgba(111, 177, 232, 0.12);
  color: #9cc7ec;
  box-shadow: none;
}
/* the unread badge's cutout ring should match the bar behind it -- the
   classic steelblue fallback rings wrong on the midnight blue gradient
   (scoped to the horizon wraps so the classic navbar keeps its own) */
:root[data-fi-theme="midnight"] .fi-tn-bell-wrap .profile-avatar-badge,
:root[data-fi-theme="midnight"] .fi-tn-avatar-wrap .profile-avatar-badge {
  box-shadow: 0 0 0 2px #142e4a;
}
:root[data-fi-theme="midnight"] .profile-theme-label { color: var(--fi-chrome-muted); }
:root[data-fi-theme="midnight"] .profile-theme-toggle {
  border-color: rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.06);
}
:root[data-fi-theme="midnight"] .profile-theme-opt { color: var(--fi-chrome-muted); }
:root[data-fi-theme="midnight"] .profile-theme-opt:hover { color: var(--fi-chrome-ink-strong); }
:root[data-fi-theme="midnight"] .profile-theme-opt--active,
:root[data-fi-theme="midnight"] .profile-theme-opt--active:hover { color: #fff; }
:root[data-fi-theme="midnight"] .profile-theme-swatch {
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.25);
}
:root[data-fi-theme="midnight"] .profile-theme-opt--active .profile-theme-swatch {
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.65);
}
@media (max-width: 991px) {
  /* centered brand lockup (ios-style title): the bar is the positioning
     context; pointer-events off so the mark never reads as tappable next
     to the real buttons */
  .fi-tn-brand {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    margin-left: 0;
    pointer-events: none;
  }
  .fi-tn-brand-logo { width: 30px; height: 19px; }
  /* with help desktop-only the ~164px lockup clears the fa + avatar cluster
     down to ~360px wide, so the wordmark rides along on phones */
  .fi-tn-brand-name { font-size: 15px; }
  .fi-tn-bell-wrap { display: none; }
}
</style>

<style>
/* midnight: the notification cards inside the bell popover / account panel
   come from fi_notification_list, whose .nc-msg-* styles are navbar globals
   inked for a light surface. the list's elements don't carry this
   component's scope attribute, so this block is unscoped on purpose; every
   rule is fenced to the two horizon containers (and classic never runs
   under midnight anyway). the type-colored pucks keep their light pastel
   fills -- they're chips and still read on dark. */
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-header,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-header { color: var(--fi-chrome-muted); }
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-card,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-card { color: var(--fi-chrome-ink); }
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-card:hover,
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-card:focus,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-card:hover,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-card:focus {
  background: var(--fi-chrome-hover-bg);
  color: var(--fi-chrome-ink);
}
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-heading,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-heading { color: var(--fi-chrome-ink-strong); }
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-preview,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-preview { color: var(--fi-chrome-text); }
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-time,
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-empty,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-time,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-empty { color: var(--fi-chrome-muted); }
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-empty .fa,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-empty .fa { color: rgba(255, 255, 255, 0.25); }
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-card-unread::before,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-card-unread::before { background: var(--fi-chrome-accent); }
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-mark-all,
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-see-all,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-mark-all,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-see-all { color: var(--fi-chrome-accent); }
[data-fi-theme="midnight"] .fi-tn-nc-pop .nc-msg-see-all:hover,
[data-fi-theme="midnight"] .fi-tn-account-panel .nc-msg-see-all:hover { color: #8fc3ee; }
</style>
